import React, { useState } from "react";
import styled from "styled-components/macro";
import { useHistory, useLocation } from "react-router-dom";
import { savePDF } from "@progress/kendo-react-pdf";
import { drawDOM, exportPDF } from "@progress/kendo-drawing";
import {
    Typography,
    Grid,
    Divider,
    Box,
    Paper,
    useTheme,
    useMediaQuery,
    Tabs,
    Tab,
    Button,
} from "@material-ui/core";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    ReferenceLine,
    ResponsiveContainer,
} from "recharts";
import { GetApp } from "@material-ui/icons";

// ==========================================
// DESKTOP STYLED COMPONENTS
// ==========================================
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #f5f5f5;
  padding: 40px 20px;
  min-height: 100vh;
  -webkit-text-size-adjust: 100% !important;
  text-size-adjust: 100% !important;

  @media print {
    background-color: white;
    padding: 0;
  }
`;

const A4Paper = styled(Paper)`
  width: 210mm;
  height: 297mm;
  padding: 10mm 15mm;
  margin-bottom: 30px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  background: white;
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media print {
    margin-bottom: 0;
    box-shadow: none;
    page-break-after: always;
    &:last-child {
      page-break-after: avoid;
    }
  }
`;

const TopBar = styled.div`
  width: 210mm;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  box-sizing: border-box;
  padding: 0 4px;

  @media print {
    display: none;
  }
`;

const MenuContainer = styled.div`
  display: flex;
  gap: 24px;
`;

const MenuItem = styled(Typography)`
  font-size: 16px !important;
  color: #78909c !important;
  font-weight: 500 !important;
  cursor: pointer;
  transition: color 0.2s;
  &:hover {
    color: #2196f3 !important;
  }
`;

const ActiveMenuItem = styled(MenuItem)`
  color: #2196f3 !important;
  font-weight: 600 !important;
`;

const DownloadButton = styled(Button)`
  text-transform: none !important;
  border: 1px solid #cfd8dc !important;
  border-radius: 6px !important;
  background-color: white !important;
  color: #546e7a !important;
  padding: 6px 14px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  box-shadow: none !important;
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;

  &:hover {
    background-color: #f5f7f8 !important;
    border-color: #b0bec5 !important;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 12px;
`;

const Title = styled(Typography)`
  font-weight: 900 !important;
  font-size: 28px !important;
  color: #0f172a;
  font-family: "Arial", "Roboto", "Helvetica", "Arial", sans-serif;
  letter-spacing: -0.5px;
`;

const Subtitle = styled(Typography)`
  color: #64748b;
  font-size: 14px !important;
  font-family: "Arial", "Roboto", "Helvetica", "Arial", sans-serif !important;
`;

const ReportIdLabel = styled(Typography)`
  color: #94a3b8;
  font-size: 11px !important;
  text-align: right;
  font-family: "Arial", "Roboto", "Helvetica", "Arial", sans-serif !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ReportId = styled(Typography)`
  font-weight: 800 !important;
  font-size: 16px !important;
  text-align: right;
  color: #0f172a;
  font-family: "Arial", "Roboto", "Helvetica", "Arial", sans-serif !important;
`;

const TagRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 16px;
  margin-bottom: 20px;
  width: 100%;
`;

const Tag = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 5px 4px;
  font-size: 10px;
  color: #64748b;
  font-weight: 500;
  background-color: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  white-space: nowrap;
  font-family: "Arial", "Roboto", "Helvetica", "Arial", sans-serif;
  flex: 1;
`;

const BlueText = styled.span`
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const HighlightBoxWrapper = styled.div`
  background: linear-gradient(90deg, #d32f2f 0%, #e53935 100%);
  border-radius: 12px;
  padding-top: 5px;
  margin-bottom: 20px;
`;

const HighlightBox = styled.div`
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
`;

const BoxLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const BoxRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 120px;
`;

const SectionTitle = styled(Typography)`
  font-weight: 800 !important;
  font-size: 14px !important;
  margin-top: 4px !important;
  margin-bottom: 14px !important;
  font-style: italic;
  color: #0f172a !important;
  font-family: "Arial", sans-serif !important;
`;

const InfoRow = styled(Grid)`
  margin-bottom: 8px !important;
`;

const InfoKey = styled(Typography)`
  font-size: 13px !important;
  color: #64748b !important;
  font-family: "Arial", sans-serif !important;
`;

const InfoValue = styled(Typography)`
  font-size: 13px !important;
  color: #0f172a !important;
  font-family: "Arial", sans-serif !important;
  font-weight: 500 !important;
`;

const InfoGrid = styled(Grid)`
  padding-left: 20px;
  box-sizing: border-box;
`;

const SectionDivider = styled(Divider)`
  margin: 16px 0 !important;
  background-color: #e2e8f0 !important;
`;

const Footer = styled.div`
  position: absolute;
  bottom: 15mm;
  left: 15mm;
  right: 15mm;
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #eee;
  padding-top: 10px;
  color: #999;
  font-size: 12px;
`;

const EnvBox = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px;
  flex: 1;
`;

const EnvHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  font-weight: bold;
  font-size: 13px;
  font-style: italic;
`;

const EnvValueRow = styled.div`
  display: flex;
  justify-content: space-between;
  text-align: center;
  margin-top: 10px;
`;

const EnvValueCol = styled.div`
  display: flex;
  flex-direction: column;
`;

const EnvLabel = styled(Typography)`
  font-size: 11px !important;
  color: #888;
`;

const EnvValue = styled(Typography)`
  font-size: 14px !important;
  font-weight: bold !important;
  color: #000;
`;

const HourlyBox = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px 16px;
  background-color: #fcfcfc;
  font-size: 13px;
  color: #666;
`;

const BulletItem = styled.li`
  font-size: 13px;
  margin-bottom: 8px;
  color: #333;
`;

// ==========================================
// MOBILE STYLED COMPONENTS
// ==========================================
const MobileContainer = styled.div`
  background-color: #fafbfc;
  min-height: calc(100vh - 64px);
  padding: 24px 20px;
  font-family: "Arial", "Roboto", "Helvetica", "Arial", sans-serif;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  -webkit-text-size-adjust: 100% !important;
  text-size-adjust: 100% !important;
`;

const Breadcrumbs = styled(Typography)`
  color: #64748b;
  font-size: 13px !important;
  margin-bottom: 24px !important;
  font-family: "Arial", "Roboto", "Helvetica", "Arial", sans-serif;
`;

const MobileSummaryCard = styled(Paper)`
  background: #ffffff;
  border-radius: 16px !important;
  padding: 24px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04) !important;
  border: 1px solid #e2e8f0;
  margin-bottom: 30px;
`;

const MobileSummaryTitle = styled(Typography)`
  font-size: 15px !important;
  font-weight: 700 !important;
  font-style: italic;
  color: #1e293b;
  margin-bottom: 24px !important;
`;

const MobileRowWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 12px;
  margin-bottom: 16px;
  border-bottom: 1.5px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const MobileRowLabel = styled(Typography)`
  font-size: 11px !important;
  font-weight: 500 !important;
  color: #94a3b8;
  text-transform: none;
  font-family: "Arial", "Roboto", "Helvetica", "Arial", sans-serif;
`;

const MobileRowValue = styled(Typography)`
  font-size: 14px !important;
  font-weight: 600 !important;
  color: #0f172a;
  margin-top: 4px !important;
  font-family: "Arial", "Roboto", "Helvetica", "Arial", sans-serif;
`;

const ButtonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: auto;
  padding: 0 4px;
`;

const MobileOpenButton = styled(Button)`
  width: 100%;
  height: 48px;
  background-color: white !important;
  border: 1.5px solid #cbd5e1 !important;
  color: #334155 !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 12px !important;
  box-shadow: none !important;
  transition: all 0.2s;

  &:hover {
    background-color: #f8fafc !important;
    border-color: #94a3b8 !important;
  }
`;

const MobileDownloadButton = styled(Button)`
  width: 100%;
  height: 48px;
  background-color: #29b6f6 !important;
  color: white !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  text-transform: none !important;
  border-radius: 12px !important;
  box-shadow: 0 4px 12px rgba(41, 182, 246, 0.3) !important;
  transition: all 0.2s;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 8px !important;

  &:hover {
    background-color: #03a9f4 !important;
  }
`;

// ==========================================
// MOCK DATA BASED ON API RESPONSE FORMAT (dummy_data.json)
// ==========================================
const mockReportData = {
    report_id: "RPT-252954",
    generated_date: 1760012400000, // 10-08-2025

    property: {
        location: {
            street: "1316 Lincoln Ct.",
            city: "Allen",
            state: "TX",
            zipCode: "75002",
            country: "USA"
        },
        building_type: "Single Family Home",
        structure_type: "Crawlspace",
        mitigation_installed: "No",
        year_built: 1994
    },

    test_info: {
        session_name: "Basement",
        room_type: "Dining Room",
        test_floor: "1st Floor",
        duration_days: 91,
        start_time: 1751938380000, // 07/08/2025 10:33 AM         
        end_time: 1759839180000    // 10/07/2025 10:33 AM         
    },

    device: {
        name: "EcoQube Apex",
        model_number: "EA200",
        serial_number: "HI01EA000021",
        manufacturer: "Ecosense (FTLab)",
        calibration_expiry_date: 1799971200000 // 01/17/2027
    },

    analysis_radon: {
        overall_avg: 4.2,
        epa_avg: 4.2,
        highest: 5.9,
        lowest: 2.9,
        uncertainty_percentage: 1.4
    },

    environmental_stats: {
        temperature: { min: 59, avg: 69, max: 72 },
        humidity: { min: 47, avg: 50, max: 62 },
        pressure: { min: 1007, avg: 1013, max: 1017 }
    },

    recommendations: {
        headline: "Action Recommended",
        details: [
            "Your overall average is above the EPA action level of 4.0 pCi/L.",
            "Continue periodic monitoring; radon levels naturally vary by season.",
            "Consider a long-term (90+ day) test annually for a fuller picture.",
            "If a future short-term test shows ≥ 4.0 pCi/L, consult a certified radon professional."
        ]
    },

    collected: (() => {
        const data = [];
        const startTime = 1751938380000;
        const durationDays = 91;
        const interval = (durationDays * 24 * 60 * 60 * 1000) / 30; // 30 points
        for (let i = 0; i < 30; i++) {
            data.push({
                timestamp: startTime + i * interval,
                radon: parseFloat((3.0 + Math.sin(i / 2) * 1.2 + Math.random() * 0.8).toFixed(1)),
                pressure: Math.floor(1008 + Math.cos(i / 3) * 5 + Math.random() * 3),
                temp: Math.floor(65 + Math.sin(i / 5) * 4 + Math.random() * 2),
                hum: Math.floor(45 + Math.cos(i / 4) * 8 + Math.random() * 4),
            });
        }
        return data;
    })()
};

// Date Format Helpers
const formatDate = (ts) => {
    const date = new Date(ts);
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
};

const formatDateShortYear = (ts) => {
    const date = new Date(ts);
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yy = String(date.getFullYear()).substring(2);
    return `${mm}/${dd}/${yy}`;
};

const formatDateTime = (ts) => {
    const date = new Date(ts);
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${mm}/${dd}/${yyyy} ${hours}:${minutes}${ampm}`;
};

const formatDateHyphen = (ts) => {
    const date = new Date(ts);
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
};

const trendData = mockReportData.collected.map((entry, index) => ({
    name: `Day ${index + 1}`,
    radon: entry.radon,
    pressure: entry.pressure,
}));

const envChartData = mockReportData.collected.map((entry) => ({
    temp: entry.temp,
    hum: entry.hum,
    pres: entry.pressure,
}));

const exportAndSavePDF = async (element, options = {}) => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const fileName = options.fileName || "report.pdf";

    let newWindow = null;
    if (isSafari && !(isIOS || isMobileDevice)) {
        newWindow = window.open("about:blank", "_blank");
    }

    try {
        const drawOptions = {
            avoidLinks: options.avoidLinks,
            forcePageBreak: options.forcePageBreak,
            keepTogether: options.keepTogether,
            margin: options.margin,
            paperSize: options.paperSize,
            landscape: options.landscape,
            repeatHeaders: options.repeatHeaders,
            scale: options.scale,
        };

        const pdfOptions = {
            author: options.author,
            creator: options.creator || 'KendoReact PDF Generator',
            date: options.date,
            imgDPI: options.imageResolution,
            keywords: options.keywords,
            landscape: options.landscape,
            margin: options.margin,
            multiPage: true,
            paperSize: options.paperSize,
            producer: options.producer,
            subject: options.subject,
            title: options.title
        };

        const group = await drawDOM(element, drawOptions);
        const dataUri = await exportPDF(group, pdfOptions);

        const base64Parts = dataUri.split(";base64,");
        const contentType = base64Parts[0].split(":")[1] || "application/pdf";
        const raw = window.atob(base64Parts[1]);
        const rawLength = raw.length;
        const uInt8Array = new Uint8Array(rawLength);
        for (let i = 0; i < rawLength; ++i) {
            uInt8Array[i] = raw.charCodeAt(i);
        }
        // Use application/octet-stream on mobile/iOS devices to force a direct download prompt instead of opening in viewer
        const blobType = (isIOS || isMobileDevice) ? "application/octet-stream" : contentType;
        const blob = new Blob([uInt8Array], { type: blobType });

        const blobUrl = URL.createObjectURL(blob);

        if (isSafari && newWindow) {
            newWindow.location.href = blobUrl;
        } else {
            const a = document.createElement("a");
            a.style.display = "none";
            a.href = blobUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
        }
    } catch (err) {
        if (newWindow) {
            newWindow.close();
        }
        throw err;
    }
};

// ==========================================
// DESKTOP VIEW COMPONENT
// ==========================================
function DesktopView({ reportId, isOffScreen }) {
    const history = useHistory();
    const location = useLocation();
    const [isDownloading, setIsDownloading] = useState(false);

    const handleDownloadPDF = async () => {
        setIsDownloading(true);
        try {
            if (document.fonts && !document.fonts.__overridden__) {
                Object.defineProperty(document.fonts, "ready", {
                    value: Promise.resolve(),
                    configurable: true,
                    writable: true,
                });
                document.fonts.__overridden__ = true;
                console.log("document.fonts.ready overridden successfully to resolve immediately");
            }
        } catch (e) {
            console.warn("Failed to override document.fonts.ready:", e);
        }

        const element = document.getElementById("pdf-report-content");
        if (element) {
            console.log("Calling exportAndSavePDF on real report element:", element);
            try {
                const finalFileName = (reportId && typeof reportId === "string" && reportId.startsWith("RPT"))
                    ? `${reportId}.pdf`
                    : `${mockReportData.report_id}.pdf`;
                await exportAndSavePDF(element, {
                    scale: 0.7,
                    paperSize: "Letter",
                    fileName: finalFileName,
                    margin: 0,
                    forcePageBreak: ".pdf-page-break",
                });
                console.log("exportAndSavePDF called successfully with filename:", finalFileName);
            } catch (err) {
                alert("PDF export error: " + err.message);
                console.error("PDF export error details:", err);
            }
        } else {
            alert("Error: pdf-report-content element not found!");
        }
        setIsDownloading(false);
    };

    return (
        <PageContainer>
            {!isOffScreen && (
                <TopBar>
                    <MenuContainer>
                        <ActiveMenuItem onClick={() => history.push(location.pathname.includes("2") ? "/reports2" : "/reports")}>
                            My Radon Analysis
                        </ActiveMenuItem>
                    </MenuContainer>
                    <DownloadButton
                        variant="outlined"
                        startIcon={isDownloading ? null : <GetApp />}
                        onClick={handleDownloadPDF}
                        disabled={isDownloading}
                    >
                        {isDownloading ? "Generating PDF..." : "Download PDF"}
                    </DownloadButton>
                </TopBar>
            )}

            <div id="pdf-report-content">
                {/* PAGE 1 */}
                <A4Paper elevation={isOffScreen ? 0 : 3}>
                    <HeaderRow>
                        <div>
                            <Title>Ecosense Radon Report</Title>
                            <Subtitle>{mockReportData.device.name} - Continuous Radon Monitor</Subtitle>
                        </div>
                        <div>
                            <ReportIdLabel>Report ID</ReportIdLabel>
                            <ReportId>{mockReportData.report_id}</ReportId>
                        </div>
                    </HeaderRow>

                    <TagRow>
                        <Tag>
                            <BlueText>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2196f3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                    <polyline points="22 4 12 14.01 9 11.01" />
                                </svg>
                                NRPP-Listed Device
                            </BlueText>
                        </Tag>
                        <Tag>
                            <BlueText>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2196f3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <polyline points="14 2 14 8 20 8" />
                                    <line x1="16" y1="13" x2="8" y2="13" />
                                    <line x1="16" y1="17" x2="8" y2="17" />
                                </svg>
                                EPA-Protocol
                            </BlueText>
                        </Tag>
                        <Tag>
                            <BlueText>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2196f3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                                </svg>
                                Continuous Radon Monitor (CRM)
                            </BlueText>
                        </Tag>
                        <Tag>
                            <BlueText>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2196f3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                    <polyline points="9 11 11 13 15 9" />
                                </svg>
                                Calibrated - exp {formatDateShortYear(mockReportData.device.calibration_expiry_date)}
                            </BlueText>
                        </Tag>
                    </TagRow>

                    <HighlightBoxWrapper>
                        <HighlightBox>
                            <BoxLeft>
                                <Typography
                                    style={{
                                        color: "#d32f2f",
                                        fontWeight: "bold",
                                        fontSize: 22,
                                        fontFamily: '"Arial", sans-serif',
                                    }}
                                >
                                    ≥ EPA Action Level
                                </Typography>
                                <Typography
                                    style={{
                                        fontSize: 11,
                                        color: "#666",
                                        maxWidth: 240,
                                        lineHeight: 1.4,
                                        fontFamily: '"Arial", sans-serif',
                                    }}
                                >
                                    EPA recommends mitigation if the radon average is 4pCi/L or more
                                </Typography>
                            </BoxLeft>
                            <BoxRight>
                                <Typography
                                    style={{
                                        fontSize: 28,
                                        fontWeight: "bold",
                                        color: "#000",
                                        lineHeight: 1.1,
                                        fontFamily: '"Arial", sans-serif',
                                    }}
                                >
                                    {mockReportData.analysis_radon.overall_avg.toFixed(1)} pCi/L
                                </Typography>
                                <Typography
                                    style={{
                                        fontSize: 12,
                                        color: "#666",
                                        fontWeight: 500,
                                        fontFamily: '"Arial", sans-serif',
                                        marginTop: 2,
                                    }}
                                >
                                    Radon Average
                                </Typography>
                            </BoxRight>
                        </HighlightBox>
                    </HighlightBoxWrapper>

                    <SectionTitle>Property Information</SectionTitle>
                    <InfoGrid container>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Address</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>
                                    {`${mockReportData.property.location.street} ${mockReportData.property.location.city} ${mockReportData.property.location.state}`}
                                </InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Building Type</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{mockReportData.property.building_type}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Structure Type</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{mockReportData.property.structure_type}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Mitigation System</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{mockReportData.property.mitigation_installed}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Year Built</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{mockReportData.property.year_built}</InfoValue>
                            </Grid>
                        </InfoRow>
                    </InfoGrid>

                    <SectionDivider />

                    <SectionTitle>Test Information</SectionTitle>
                    <InfoGrid container>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Session Name</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{mockReportData.test_info.session_name}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Test Duration</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{mockReportData.test_info.duration_days} Days</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Test Floor</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{mockReportData.test_info.test_floor}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Room Type</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{mockReportData.test_info.room_type}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Test Started</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{formatDateTime(mockReportData.test_info.start_time)}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Test Finished</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{formatDateTime(mockReportData.test_info.end_time)}</InfoValue>
                            </Grid>
                        </InfoRow>
                    </InfoGrid>

                    <SectionDivider />

                    <SectionTitle>Device Information</SectionTitle>
                    <InfoGrid container>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Device Name</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{mockReportData.device.name}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Model Number</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{mockReportData.device.model_number}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Serial Number</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{mockReportData.device.serial_number}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Manufacturer</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{mockReportData.device.manufacturer}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Calibration Exp. Date</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{formatDate(mockReportData.device.calibration_expiry_date)}</InfoValue>
                            </Grid>
                        </InfoRow>
                    </InfoGrid>

                    <Footer>
                        <span>Report ID: {mockReportData.report_id} - Generated {formatDateHyphen(mockReportData.generated_date)}</span>
                        <span>Page 1 of 3</span>
                    </Footer>
                </A4Paper>

                {/* PAGE 2 */}
                <A4Paper elevation={3} className="pdf-page-break">
                    <SectionTitle style={{ marginTop: 0 }}>
                        Daily Radon Trend
                    </SectionTitle>
                    <Typography style={{ fontSize: 12, color: "#888", marginBottom: 20 }}>
                        Daily averages over the 91-day long-term test (07/08/2025 –
                        10/07/2025). Min/max statistics below reflect hourly readings.
                    </Typography>

                    <Box style={{ height: 180, width: "100%", marginBottom: 10, display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <LineChart
                            width={650}
                            height={180}
                            data={trendData}
                            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis
                                dataKey="name"
                                tick={{ fontSize: 10 }}
                                tickLine={false}
                                axisLine={false}
                            />
                            <YAxis
                                yAxisId="left"
                                tick={{ fontSize: 10 }}
                                tickLine={false}
                                axisLine={false}
                                domain={[0, 8]}
                                label={{
                                    value: "(pCi/L)",
                                    position: "top",
                                    offset: 10,
                                    fontSize: 10,
                                    fill: "#888",
                                }}
                            />
                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                tick={{ fontSize: 10 }}
                                tickLine={false}
                                axisLine={false}
                                domain={[1000, 1020]}
                                label={{
                                    value: "(hPa)",
                                    position: "top",
                                    offset: 10,
                                    fontSize: 10,
                                    fill: "#888",
                                }}
                            />
                            <ReferenceLine
                                y={4}
                                yAxisId="left"
                                stroke="#f44336"
                                strokeDasharray="3 3"
                                label={{
                                    position: "left",
                                    value: "EPA 4.0",
                                    fill: "#f44336",
                                    fontSize: 10,
                                }}
                            />
                            <Line
                                yAxisId="left"
                                type="monotone"
                                dataKey="radon"
                                stroke="#2196F3"
                                dot={false}
                                strokeWidth={2}
                                name="Radon (pCi/L)"
                            />
                            <Line
                                yAxisId="right"
                                type="monotone"
                                dataKey="pressure"
                                stroke="#9C27B0"
                                strokeDasharray="5 5"
                                dot={false}
                                strokeWidth={1}
                                name="Pressure (hPa)"
                            />
                        </LineChart>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                gap: 20,
                                marginTop: 10,
                            }}
                        >
                            <Typography
                                style={{ fontSize: 11, display: "flex", alignItems: "center" }}
                            >
                                <span
                                    style={{
                                        display: "inline-block",
                                        width: 20,
                                        height: 2,
                                        backgroundColor: "#2196F3",
                                        marginRight: 5,
                                    }}
                                ></span>{" "}
                                Radon (pCi/L)
                            </Typography>
                            <Typography
                                style={{ fontSize: 11, display: "flex", alignItems: "center" }}
                            >
                                <span
                                    style={{
                                        display: "inline-block",
                                        width: 20,
                                        height: 2,
                                        borderBottom: "1px dashed #9C27B0",
                                        marginRight: 5,
                                    }}
                                ></span>{" "}
                                Pressure (hPa)
                            </Typography>
                            <Typography
                                style={{ fontSize: 11, display: "flex", alignItems: "center" }}
                            >
                                <span
                                    style={{
                                        display: "inline-block",
                                        width: 20,
                                        height: 2,
                                        borderBottom: "1px dotted #f44336",
                                        marginRight: 5,
                                    }}
                                ></span>{" "}
                                EPA Action Level
                            </Typography>
                        </div>
                    </Box>

                    <SectionDivider />

                    <SectionTitle>Radon Concentration</SectionTitle>
                    <Grid container>
                        <InfoRow item xs={12} container>
                            <Grid item xs={6}>
                                <InfoKey>Measurement Radon Concentration Max</InfoKey>
                            </Grid>
                            <Grid item xs={6}>
                                <InfoValue>{mockReportData.analysis_radon.highest.toFixed(1)} pCi/L</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={6}>
                                <InfoKey>Measurement Radon Concentration Average</InfoKey>
                            </Grid>
                            <Grid item xs={6}>
                                <InfoValue>{mockReportData.analysis_radon.overall_avg.toFixed(1)} pCi/L</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={6}>
                                <InfoKey>Measurement Radon Concentration Min</InfoKey>
                            </Grid>
                            <Grid item xs={6}>
                                <InfoValue>{mockReportData.analysis_radon.lowest.toFixed(1)} pCi/L</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={6}>
                                <InfoKey>% Uncertainty</InfoKey>
                            </Grid>
                            <Grid item xs={6}>
                                <InfoValue>± {mockReportData.analysis_radon.uncertainty_percentage.toFixed(1)}%</InfoValue>
                            </Grid>
                        </InfoRow>
                    </Grid>

                    <SectionDivider />

                    <SectionTitle>Environmental Conditions ({mockReportData.device.name})</SectionTitle>
                    <Typography style={{ fontSize: 12, color: "#888", marginBottom: 16 }}>
                        Min / avg / max temperature, humidity, and barometric pressure
                        reflect hourly readings across the test window.
                    </Typography>

                    <div style={{ display: "flex", gap: 16, marginBottom: 10 }}>
                        {/* Temperature */}
                        <EnvBox>
                            <EnvHeader>
                                <span>Temperature</span>
                                <span style={{ color: "#888", fontWeight: "normal" }}>°F</span>
                            </EnvHeader>
                            <Box style={{ height: 40, width: "100%", display: "flex", justifyContent: "center" }}>
                                <LineChart width={196} height={40} data={envChartData}>
                                    <Line
                                        type="monotone"
                                        dataKey="temp"
                                        stroke="#FF9800"
                                        dot={false}
                                        strokeWidth={2}
                                    />
                                </LineChart>
                            </Box>
                            <EnvValueRow>
                                <EnvValueCol>
                                    <EnvLabel>MIN</EnvLabel>
                                    <EnvValue>{mockReportData.environmental_stats.temperature.min}</EnvValue>
                                </EnvValueCol>
                                <EnvValueCol>
                                    <EnvLabel>AVG</EnvLabel>
                                    <EnvValue>{mockReportData.environmental_stats.temperature.avg}</EnvValue>
                                </EnvValueCol>
                                <EnvValueCol>
                                    <EnvLabel>MAX</EnvLabel>
                                    <EnvValue>{mockReportData.environmental_stats.temperature.max}</EnvValue>
                                </EnvValueCol>
                            </EnvValueRow>
                        </EnvBox>

                        {/* Humidity */}
                        <EnvBox>
                            <EnvHeader>
                                <span>Humidity</span>
                                <span style={{ color: "#888", fontWeight: "normal" }}>
                                    % RH
                                </span>
                            </EnvHeader>
                            <Box style={{ height: 40, width: "100%", display: "flex", justifyContent: "center" }}>
                                <LineChart width={196} height={40} data={envChartData}>
                                    <Line
                                        type="monotone"
                                        dataKey="hum"
                                        stroke="#03A9F4"
                                        dot={false}
                                        strokeWidth={2}
                                    />
                                </LineChart>
                            </Box>
                            <EnvValueRow>
                                <EnvValueCol>
                                    <EnvLabel>MIN</EnvLabel>
                                    <EnvValue>{mockReportData.environmental_stats.humidity.min}</EnvValue>
                                </EnvValueCol>
                                <EnvValueCol>
                                    <EnvLabel>AVG</EnvLabel>
                                    <EnvValue>{mockReportData.environmental_stats.humidity.avg}</EnvValue>
                                </EnvValueCol>
                                <EnvValueCol>
                                    <EnvLabel>MAX</EnvLabel>
                                    <EnvValue>{mockReportData.environmental_stats.humidity.max}</EnvValue>
                                </EnvValueCol>
                            </EnvValueRow>
                        </EnvBox>

                        {/* Pressure */}
                        <EnvBox>
                            <EnvHeader>
                                <span>Pressure</span>
                                <span style={{ color: "#888", fontWeight: "normal" }}>hPa</span>
                            </EnvHeader>
                            <Box style={{ height: 40, width: "100%", display: "flex", justifyContent: "center" }}>
                                <LineChart width={196} height={40} data={envChartData}>
                                    <Line
                                        type="monotone"
                                        dataKey="pres"
                                        stroke="#673AB7"
                                        dot={false}
                                        strokeWidth={2}
                                    />
                                </LineChart>
                            </Box>
                            <EnvValueRow>
                                <EnvValueCol>
                                    <EnvLabel>MIN</EnvLabel>
                                    <EnvValue>{mockReportData.environmental_stats.pressure.min}</EnvValue>
                                </EnvValueCol>
                                <EnvValueCol>
                                    <EnvLabel>AVG</EnvLabel>
                                    <EnvValue>{mockReportData.environmental_stats.pressure.avg}</EnvValue>
                                </EnvValueCol>
                                <EnvValueCol>
                                    <EnvLabel>MAX</EnvLabel>
                                    <EnvValue>{mockReportData.environmental_stats.pressure.max}</EnvValue>
                                </EnvValueCol>
                            </EnvValueRow>
                        </EnvBox>
                    </div>

                    <SectionDivider />

                    <SectionTitle>Hourly Measurement Data</SectionTitle>
                    <HourlyBox>
                        Hourly radon, temperature, humidity, and pressure readings are
                        available in the <strong>Ecosense app</strong> under this session.
                        The certified report summarizes daily averages to communicate
                        long-term exposure for the {mockReportData.test_info.duration_days}-day test.
                    </HourlyBox>

                    <Footer>
                        <span>Report ID: {mockReportData.report_id} - Generated {formatDateHyphen(mockReportData.generated_date)}</span>
                        <span>Page 2 of 3</span>
                    </Footer>
                </A4Paper>

                {/* PAGE 3 */}
                <A4Paper elevation={3} className="pdf-page-break">
                    <SectionTitle style={{ marginTop: 0 }}>
                        Recommended Actions
                    </SectionTitle>
                    <ul style={{ paddingLeft: 20, margin: 0, marginBottom: 10 }}>
                        {mockReportData.recommendations.details.map((detail, idx) => (
                            <BulletItem key={idx}>{detail}</BulletItem>
                        ))}
                    </ul>

                    <SectionDivider />

                    <SectionTitle>Methodology & Protocol</SectionTitle>
                    <Typography
                        style={{
                            fontSize: 13,
                            color: "#666",
                            marginBottom: 10,
                            lineHeight: 1.5,
                        }}
                    >
                        This test followed the EPA Protocol for Short-Term Radon Measurement
                        in Homes. Closed-house conditions require all windows and exterior
                        doors closed (except normal entry/exit) for at least 12 hours before
                        and during the test. The first four hours of data are discarded per
                        EPA protocol. Uncertainty is calculated from the device's
                        continuous-monitor counting statistics combined with environmental
                        variability.
                    </Typography>

                    <SectionDivider />

                    <SectionTitle>Test Limitation & Disclaimer</SectionTitle>
                    <Typography
                        style={{
                            fontSize: 13,
                            color: "#666",
                            marginBottom: 10,
                            lineHeight: 1.5,
                        }}
                    >
                        There is an uncertainty with any measurement result due to
                        statistical variations and other factors such as daily and seasonal
                        variations in radon concentrations. Variations may be due to changes
                        in the weather, operation of the dwelling, or possible interference
                        with the necessary test conditions. Temperature and humidity can
                        vary depending on environmental conditions. The test data was taken
                        from a testing device approved by the National Radon Proficiency
                        Program.
                    </Typography>
                    <Typography style={{ fontSize: 13, color: "#666", lineHeight: 1.5 }}>
                        This consumer-generated report is provided for informational use.
                        Some jurisdictions require a licensed radon measurement professional
                        to conduct tests used in real-estate transactions — please verify
                        local requirements.
                    </Typography>

                    <Footer>
                        <span>Report ID: {mockReportData.report_id} - Generated {formatDateHyphen(mockReportData.generated_date)}</span>
                        <span>Page 3 of 3</span>
                    </Footer>
                </A4Paper>
            </div>
        </PageContainer>
    );
}

// ==========================================
// MOBILE VIEW COMPONENT
// ==========================================
function MobileView({ reportId }) {
    const [showFullReport, setShowFullReport] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const handleDownloadPDF = async () => {
        setIsDownloading(true);
        try {
            if (document.fonts && !document.fonts.__overridden__) {
                Object.defineProperty(document.fonts, "ready", {
                    value: Promise.resolve(),
                    configurable: true,
                    writable: true,
                });
                document.fonts.__overridden__ = true;
                console.log("document.fonts.ready overridden successfully to resolve immediately");
            }
        } catch (e) {
            console.warn("Failed to override document.fonts.ready:", e);
        }

        const element = document.getElementById("pdf-report-content");
        if (element) {
            console.log("Calling exportAndSavePDF on mobile element:", element);
            try {
                const finalFileName = (reportId && typeof reportId === "string" && reportId.startsWith("RPT"))
                    ? `${reportId}.pdf`
                    : `${mockReportData.report_id}.pdf`;
                await exportAndSavePDF(element, {
                    scale: 0.75,
                    paperSize: "A4",
                    fileName: finalFileName,
                    margin: 0,
                    forcePageBreak: ".pdf-page-break",
                });
                console.log("exportAndSavePDF called successfully with filename:", finalFileName);
            } catch (err) {
                alert("PDF export error: " + err.message);
                console.error("PDF export error details:", err);
            }
        } else {
            alert("Error: pdf-report-content element not found!");
        }
        setIsDownloading(false);
    };

    if (showFullReport) {
        return (
            <div style={{ backgroundColor: "#f5f5f5", minHeight: "100vh", padding: "16px" }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="16px">
                    <Button
                        variant="outlined"
                        onClick={() => setShowFullReport(false)}
                        style={{ textTransform: "none", backgroundColor: "white", borderColor: "#cbd5e1", borderRadius: 8 }}
                    >
                        ← Back
                    </Button>
                    <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
                        Detailed Report Preview
                    </Typography>
                </Box>
                <div style={{ overflowX: "auto", width: "100%", paddingBottom: "20px" }}>
                    <div style={{ width: "210mm" }}>
                        <DesktopView reportId={reportId} isOffScreen={false} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <MobileContainer>
            <Breadcrumbs>
                My Reports / Session / <strong style={{ color: "#1e293b", fontWeight: 600 }}>{mockReportData.test_info.session_name}</strong>
            </Breadcrumbs>

            <MobileSummaryCard>
                <MobileSummaryTitle>Ecosense Radon Report Summary</MobileSummaryTitle>

                <MobileRowWrapper>
                    <MobileRowLabel>Session Name</MobileRowLabel>
                    <MobileRowValue>{mockReportData.test_info.session_name}</MobileRowValue>
                </MobileRowWrapper>

                <MobileRowWrapper>
                    <MobileRowLabel>Radon Average</MobileRowLabel>
                    <MobileRowValue>{mockReportData.analysis_radon.overall_avg.toFixed(1)} pCi/L</MobileRowValue>
                </MobileRowWrapper>

                <MobileRowWrapper>
                    <MobileRowLabel>Test Period</MobileRowLabel>
                    <MobileRowValue>
                        {`${formatDate(mockReportData.test_info.start_time)} - ${formatDate(mockReportData.test_info.end_time)}`}
                    </MobileRowValue>
                </MobileRowWrapper>

                <MobileRowWrapper>
                    <MobileRowLabel>Test Floor</MobileRowLabel>
                    <MobileRowValue>{mockReportData.test_info.test_floor}</MobileRowValue>
                </MobileRowWrapper>

                <MobileRowWrapper>
                    <MobileRowLabel>Requested</MobileRowLabel>
                    <MobileRowValue>{formatDate(mockReportData.generated_date)}</MobileRowValue>
                </MobileRowWrapper>
            </MobileSummaryCard>

            <ButtonStack>
                <MobileOpenButton variant="outlined" onClick={() => setShowFullReport(true)}>
                    Open Report
                </MobileOpenButton>
                <MobileDownloadButton variant="contained" onClick={handleDownloadPDF} disabled={isDownloading}>
                    {isDownloading ? null : <GetApp style={{ fontSize: 20 }} />}
                    {isDownloading ? "Generating PDF..." : "Download PDF Report"}
                </MobileDownloadButton>
            </ButtonStack>
        </MobileContainer>
    );
}

export default function ReportEcosense() {
    const theme = useTheme();
    // Use MUI's useMediaQuery to determine if screen size is sm or smaller (mobile)
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const location = useLocation();
    const { reportId } = location.state || {};
    const currentReportId = reportId || "RPT-252954";

    if (isMobile) {
        return (
            <>
                <MobileView reportId={currentReportId} />
                {/* Render DesktopView off-screen just for PDF download on mobile */}
                <div style={{ position: "absolute", left: "-9999px", top: "-9999px", WebkitTextSizeAdjust: "100%", textSizeAdjust: "100%" }}>
                    <DesktopView reportId={currentReportId} isOffScreen={true} />
                </div>
            </>
        );
    }

    return <DesktopView reportId={currentReportId} />;
}
